/**
 * Pure helpers to resolve issue detail field values for display.
 * Handles value extraction (fields / mappedFields), formatting,
 * per-field transformations / default values and "unset" detection.
 */

type FieldsMapping = Record<string, string>;

interface IssueLike {
  fields?: Record<string, unknown>;
  mappedFields?: Record<string, unknown>;
  names?: Record<string, string>;
}

export interface ResolvedDetailField {
  key: string;
  label: string;
  /** Original value, kept for ADF document rendering */
  rawValue: unknown;
  /** Formatted string ready to display, or null when nothing should be shown */
  displayValue: string | null;
  /** Whether the raw value is an Atlassian Document Format doc */
  isADFDoc: boolean;
  /** Whether the field is unset: missing, empty, or an equivalent placeholder */
  isEmpty: boolean;
}

/**
 * Per logical-key rules:
 * - `emptyValues`: values treated as unset (e.g. an "HH:mm" placeholder)
 * - `emptyLabel`: label shown when the field is unset (omit to hide the row)
 */
const FIELD_RULES: Record<string, { emptyValues?: string[]; emptyLabel?: string }> = {
  // `emptyLabel` is a stable English i18n key; the consumer localizes it via a
  // literal-keyed resolver at the render site (kept out of this pure helper).
  intervention_time: { emptyValues: ['HH:mm'], emptyLabel: 'Not specified' },
};

const isADFDocument = (value: unknown): boolean =>
  !!value && typeof value === 'object' && (value as { type?: string }).type === 'doc';

/** Flatten an object/array Jira value into a readable string (always string | null). */
const formatValue = (value: unknown): string | null => {
  if (value === undefined || value === null || value === '') return null;

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map((v) => formatValue(v)).filter(Boolean).join(', ') || null;
    }
    const v = value as Record<string, unknown>;
    // Pick the first present candidate (keeps 0 / false), then coerce to a string
    const candidate = [v.name, v.label, v.displayName, v.value].find((c) => c !== undefined && c !== null);
    if (candidate !== undefined) {
      const formatted = typeof candidate === 'object' ? formatValue(candidate) : String(candidate);
      if (formatted !== null && formatted !== '') return formatted;
    }
    if (Array.isArray(v.values)) return formatValue(v.values);
    return null;
  }

  return String(value);
};

/** Human-readable label for a field, falling back to the prettified logical key. */
const toLabel = (logicalKey: string, names: Record<string, string> | undefined, rawFieldName: string): string => {
  const fallback = logicalKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  return names?.[rawFieldName] || fallback || logicalKey;
};

/** Resolve a single detail field from its logical key. */
export const resolveDetailField = (
  logicalKey: string,
  issue: IssueLike,
  fieldsMapping: FieldsMapping = {}
): ResolvedDetailField => {
  const fieldId = fieldsMapping[logicalKey] || logicalKey;
  const rawValue = issue?.fields?.[fieldId] || issue?.mappedFields?.[logicalKey] || null;
  const rule = FIELD_RULES[logicalKey];
  const label = toLabel(logicalKey, issue?.names, fieldId);

  if (isADFDocument(rawValue)) {
    return { key: logicalKey, label, rawValue, displayValue: null, isADFDoc: true, isEmpty: false };
  }

  const formatted = formatValue(rawValue);
  const isPlaceholder = formatted !== null && !!rule?.emptyValues?.includes(formatted);
  const isEmpty = formatted === null || isPlaceholder;
  const displayValue = isEmpty ? rule?.emptyLabel ?? null : formatted;

  return { key: logicalKey, label, rawValue, displayValue, isADFDoc: false, isEmpty };
};

/** Resolve a list of logical keys into displayable fields. */
export const resolveDetailFields = (
  keys: string[],
  issue: IssueLike,
  fieldsMapping: FieldsMapping = {}
): ResolvedDetailField[] => keys.map((key) => resolveDetailField(key, issue, fieldsMapping));

/** Whether a block holding these fields has at least one filled value. */
export const hasVisibleField = (fields: ResolvedDetailField[]): boolean => fields.some((f) => !f.isEmpty);
